import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Plus, Pencil, Trash2, HelpCircle, X, Save, ChevronDown, ChevronUp, FolderPlus, Loader2, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import LanguageTabs, { useAvailableLanguages, getContentWithFallback } from '../../components/admin/LanguageTabs';

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback categories if none in DB
const DEFAULT_CATEGORIES = [
  { slug: 'general', name: { pl: 'Ogólne', en: 'General' } },
  { slug: 'booking', name: { pl: 'Rezerwacje', en: 'Booking' } },
  { slug: 'passes', name: { pl: 'Karnety', en: 'Passes' } },
];

const AdminFAQ = () => {
  const { getAuthHeader } = useAdminAuth();
  const { languages, loading: languagesLoading, defaultLanguage } = useAvailableLanguages();
  
  const [activeTab, setActiveTab] = useState('faq'); // 'faq' | 'categories'
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeLangTab, setActiveLangTab] = useState('pl');

  const emptyFaq = {
    question: {},
    answer: {},
    category: 'general',
    sortOrder: 0,
    isPublished: true
  };

  const emptyCategory = {
    slug: '',
    name: {},
    sortOrder: 0,
    isActive: true
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (defaultLanguage) {
      setActiveLangTab(defaultLanguage);
    }
  }, [defaultLanguage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [faqRes, catRes] = await Promise.all([
        axios.get(`${API}/api/admin/faq?all=true`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/admin/faq-categories`, { headers: getAuthHeader() })
      ]);
      setFaqs(faqRes.data);
      
      // Use fetched categories or defaults
      if (catRes.data && catRes.data.length > 0) {
        setCategories(catRes.data);
      } else {
        setCategories(DEFAULT_CATEGORIES);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Nie udało się pobrać danych');
      setCategories(DEFAULT_CATEGORIES);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/faq-categories`, { headers: getAuthHeader() });
      if (res.data && res.data.length > 0) {
        setCategories(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleSaveFaq = async () => {
    try {
      if (isCreating) {
        await axios.post(`${API}/api/admin/faq`, editingFaq, { headers: getAuthHeader() });
        toast.success('FAQ utworzone');
      } else {
        await axios.put(`${API}/api/admin/faq/${editingFaq.faq_id}`, editingFaq, { headers: getAuthHeader() });
        toast.success('FAQ zaktualizowane');
      }
      setEditingFaq(null);
      setIsCreating(false);
      fetchData();
    } catch (error) {
      toast.error('Nie udało się zapisać');
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!confirm('Czy na pewno chcesz usunąć to FAQ?')) return;
    try {
      await axios.delete(`${API}/api/admin/faq/${faqId}`, { headers: getAuthHeader() });
      toast.success('FAQ usunięte');
      fetchData();
    } catch (error) {
      toast.error('Nie udało się usunąć');
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (!editingCategory.slug || !editingCategory.name?.pl) {
        toast.error('Slug i nazwa (PL) są wymagane');
        return;
      }
      
      if (isCreating) {
        await axios.post(`${API}/api/admin/faq-categories`, editingCategory, { headers: getAuthHeader() });
        toast.success('Kategoria utworzona');
      } else {
        await axios.put(`${API}/api/admin/faq-categories/${editingCategory.category_id}`, editingCategory, { headers: getAuthHeader() });
        toast.success('Kategoria zaktualizowana');
      }
      setEditingCategory(null);
      setIsCreating(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się zapisać kategorii');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Czy na pewno chcesz usunąć tę kategorię?')) return;
    try {
      await axios.delete(`${API}/api/admin/faq-categories/${categoryId}`, { headers: getAuthHeader() });
      toast.success('Kategoria usunięta');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się usunąć kategorii');
    }
  };

  const handleInitCategories = async () => {
    try {
      await axios.post(`${API}/api/admin/faq-categories/init`, {}, { headers: getAuthHeader() });
      toast.success('Kategorie domyślne utworzone');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nie udało się utworzyć kategorii');
    }
  };

  const getCategoryName = (slug, lang = 'pl') => {
    const cat = categories.find(c => c.slug === slug || c.category_id === slug);
    if (!cat) return slug;
    return getContentWithFallback(cat.name, lang, 'pl') || slug;
  };

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    const cat = faq.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(faq);
    return acc;
  }, {});

  if (loading || languagesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Zarządzanie FAQ</h1>
          <p className="text-gray-400">Najczęściej zadawane pytania i kategorie</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white/5 border-white/10">
          <TabsTrigger value="faq" className="data-[state=active]:bg-purple-600">
            <HelpCircle className="w-4 h-4 mr-2" />
            Pytania FAQ
          </TabsTrigger>
          <TabsTrigger value="categories" className="data-[state=active]:bg-purple-600">
            <Settings2 className="w-4 h-4 mr-2" />
            Kategorie
          </TabsTrigger>
        </TabsList>

        {/* FAQ Tab */}
        <TabsContent value="faq" className="mt-6 space-y-6">
          <div className="flex justify-end">
            <Button
              onClick={() => { setEditingFaq(emptyFaq); setIsCreating(true); }}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Dodaj FAQ
            </Button>
          </div>

          {/* FAQ List by Category */}
          {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
            <div key={category} className="space-y-3">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                {getCategoryName(category)}
                <span className="text-gray-500 text-sm font-normal">({categoryFaqs.length})</span>
              </h2>
              
              <div className="space-y-2">
                {categoryFaqs.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)).map((faq) => (
                  <Card key={faq.faq_id} className="bg-white/5 border-white/10">
                    <CardContent className="p-0">
                      <div 
                        className="p-4 flex items-center justify-between cursor-pointer"
                        onClick={() => setExpandedFaq(expandedFaq === faq.faq_id ? null : faq.faq_id)}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <HelpCircle className="w-5 h-5 text-purple-400 flex-shrink-0" />
                          <span className="text-white truncate">
                            {getContentWithFallback(faq.question, 'pl') || 'Bez pytania'}
                          </span>
                          {!faq.isPublished && (
                            <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
                              Ukryty
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); setEditingFaq(faq); setIsCreating(false); }}
                            className="text-gray-400 hover:text-white"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleDeleteFaq(faq.faq_id); }}
                            className="text-gray-400 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          {expandedFaq === faq.faq_id ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {expandedFaq === faq.faq_id && (
                        <div className="px-4 pb-4 pt-0 border-t border-white/10 mt-0">
                          <div className="pt-4 text-gray-400 text-sm whitespace-pre-wrap">
                            {getContentWithFallback(faq.answer, 'pl') || 'Brak odpowiedzi'}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}

          {faqs.length === 0 && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8 text-center">
                <HelpCircle className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                <p className="text-gray-400">Brak FAQ</p>
                <Button
                  onClick={() => { setEditingFaq(emptyFaq); setIsCreating(true); }}
                  variant="outline"
                  className="mt-4"
                >
                  Dodaj pierwsze pytanie
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-6 space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-gray-400 text-sm">
              Zarządzaj kategoriami FAQ. Nazwy kategorii są wielojęzyczne.
            </p>
            <div className="flex gap-2">
              {categories.length === 0 && (
                <Button
                  onClick={handleInitCategories}
                  variant="outline"
                  className="border-purple-500 text-purple-400"
                >
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Inicjalizuj domyślne
                </Button>
              )}
              <Button
                onClick={() => { setEditingCategory(emptyCategory); setIsCreating(true); }}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Dodaj kategorię
              </Button>
            </div>
          </div>

          <div className="grid gap-4">
            {categories.map((cat) => (
              <Card key={cat.category_id || cat.slug} className="bg-white/5 border-white/10">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <Settings2 className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">
                        {getContentWithFallback(cat.name, 'pl')}
                      </h3>
                      <p className="text-gray-500 text-sm">Slug: {cat.slug}</p>
                    </div>
                    {cat.isActive === false && (
                      <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
                        Nieaktywna
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setEditingCategory(cat); setIsCreating(false); }}
                      className="text-gray-400 hover:text-white"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteCategory(cat.category_id)}
                      className="text-gray-400 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {categories.length === 0 && (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-8 text-center">
                  <Settings2 className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                  <p className="text-gray-400">Brak kategorii FAQ</p>
                  <p className="text-gray-500 text-sm mt-2">Użyj przycisku "Inicjalizuj domyślne" aby utworzyć podstawowe kategorie</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* FAQ Edit Modal */}
      {editingFaq && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-gray-900 z-10">
              <CardTitle className="text-white">
                {isCreating ? 'Nowe FAQ' : 'Edytuj FAQ'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingFaq(null); setIsCreating(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Category & Order */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Kategoria</Label>
                  <Select
                    value={editingFaq.category || 'general'}
                    onValueChange={(value) => setEditingFaq({ ...editingFaq, category: value })}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.category_id || cat.slug} value={cat.slug || cat.category_id}>
                          {getContentWithFallback(cat.name, 'pl')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Kolejność</Label>
                  <Input
                    type="number"
                    value={editingFaq.sortOrder || 0}
                    onChange={(e) => setEditingFaq({ ...editingFaq, sortOrder: parseInt(e.target.value) })}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Language Tabs */}
              <div className="space-y-4">
                <Label className="text-gray-300">Treść (wybierz język)</Label>
                <LanguageTabs
                  activeTab={activeLangTab}
                  onTabChange={setActiveLangTab}
                  showIndicator={true}
                  hasContent={{
                    ...Object.fromEntries(languages.map(l => [l.code, !!editingFaq.question?.[l.code]]))
                  }}
                />
                
                {/* Question */}
                <div className="space-y-2">
                  <Label className="text-gray-400 text-sm">Pytanie ({activeLangTab.toUpperCase()})</Label>
                  <Textarea
                    value={editingFaq.question?.[activeLangTab] || ''}
                    onChange={(e) => setEditingFaq({ 
                      ...editingFaq, 
                      question: { ...editingFaq.question, [activeLangTab]: e.target.value } 
                    })}
                    rows={2}
                    placeholder={`Wprowadź pytanie w języku ${activeLangTab.toUpperCase()}`}
                    className="bg-white/5 border-white/10 text-white resize-none"
                  />
                </div>
                
                {/* Answer */}
                <div className="space-y-2">
                  <Label className="text-gray-400 text-sm">Odpowiedź ({activeLangTab.toUpperCase()})</Label>
                  <Textarea
                    value={editingFaq.answer?.[activeLangTab] || ''}
                    onChange={(e) => setEditingFaq({ 
                      ...editingFaq, 
                      answer: { ...editingFaq.answer, [activeLangTab]: e.target.value } 
                    })}
                    rows={5}
                    placeholder={`Wprowadź odpowiedź w języku ${activeLangTab.toUpperCase()}`}
                    className="bg-white/5 border-white/10 text-white resize-none"
                  />
                </div>
              </div>

              {/* Published */}
              <div className="flex items-center gap-3">
                <Switch
                  checked={editingFaq.isPublished}
                  onCheckedChange={(checked) => setEditingFaq({ ...editingFaq, isPublished: checked })}
                />
                <Label className="text-gray-300">Opublikowany</Label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingFaq(null); setIsCreating(false); }}>
                  Anuluj
                </Button>
                <Button onClick={handleSaveFaq} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Category Edit Modal */}
      {editingCategory && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <Card className="bg-gray-900 border-white/10 w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">
                {isCreating ? 'Nowa kategoria' : 'Edytuj kategorię'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => { setEditingCategory(null); setIsCreating(false); }}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Slug */}
              <div className="space-y-2">
                <Label className="text-gray-300">Slug (identyfikator)</Label>
                <Input
                  value={editingCategory.slug || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="np. booking, passes"
                  className="bg-white/5 border-white/10 text-white"
                  disabled={!isCreating}
                />
              </div>

              {/* Sort Order */}
              <div className="space-y-2">
                <Label className="text-gray-300">Kolejność</Label>
                <Input
                  type="number"
                  value={editingCategory.sortOrder || 0}
                  onChange={(e) => setEditingCategory({ ...editingCategory, sortOrder: parseInt(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Language Tabs for Name */}
              <div className="space-y-4">
                <Label className="text-gray-300">Nazwa kategorii (wybierz język)</Label>
                <LanguageTabs
                  activeTab={activeLangTab}
                  onTabChange={setActiveLangTab}
                  size="small"
                />
                
                <Input
                  value={editingCategory.name?.[activeLangTab] || ''}
                  onChange={(e) => setEditingCategory({ 
                    ...editingCategory, 
                    name: { ...editingCategory.name, [activeLangTab]: e.target.value } 
                  })}
                  placeholder={`Nazwa w języku ${activeLangTab.toUpperCase()}`}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              {/* Active */}
              <div className="flex items-center gap-3">
                <Switch
                  checked={editingCategory.isActive !== false}
                  onCheckedChange={(checked) => setEditingCategory({ ...editingCategory, isActive: checked })}
                />
                <Label className="text-gray-300">Aktywna</Label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button variant="outline" onClick={() => { setEditingCategory(null); setIsCreating(false); }}>
                  Anuluj
                </Button>
                <Button onClick={handleSaveCategory} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Zapisz
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminFAQ;
